import axios from "axios";
import { BASE_URL, REFRESH_TOKEN } from "./endpoints";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Create an Axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    if (!config.url.includes("login")) {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });
  failedQueue = [];
};

// Function to setup interceptor with enhanced error handling
export const setupInterceptors = (showToast) => {
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      console.log('error===>',error.response.status)
      if (!error.response) {
        showToast("Network error. Check your internet connection.", "warning");
      } else {
        switch (error.response.status) {
          case 400:
            showToast("Bad Request. Please check your input.", "error");
            break;
            case 401:
            showToast("Unauthorized. Please log in again.", "error");
            break;
          case 403:
            showToast("Forbidden. You don't have permission.", "error");
            break;
          case 404:
            showToast("Resource not found. Please try again.", "error");
            break;
          case 415:
            showToast("Unsupported media type. Please check your request format.", "error");
            break;
          case 429:
            showToast("Too many requests. Please slow down.", "warning");
            break;
          case 500:
            showToast("Server error. Please try again later.", "error");
            break;
          case 502:
            showToast("Bad gateway. Server is unreachable.", "error");
            break;
          case 503:
            showToast("Service unavailable. Try again later.", "warning");
            break;
          case 504:
            showToast("Gateway timeout. Server took too long to respond.", "error");
            break;
          default:
            showToast("An unexpected error occurred. Please try again.", "error");
            break;
        }
      }
      return Promise.reject(error);
    }
  );
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      // **Handle specific error cases**
      if (error.response?.status === 500) {
        toast.error("Server error. Please try again later.");
      } else if (!error.response) {
        toast.warn("Network error. Please check your internet connection.");
      }

      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = localStorage.getItem("refresh_token");
          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          const response = await axios.post(`${BASE_URL}${REFRESH_TOKEN}`, {
            refresh_token: refreshToken,
          });

          const newAccessToken = response.data?.data?.access_token;
          localStorage.setItem("access_token", newAccessToken);
          processQueue(null, newAccessToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);

          if (refreshError.response?.status === 401) {
            alert("Refresh token expired or invalid. Logging out.");
          } else {
            console.error("An error occurred while refreshing token:", refreshError);
          }

          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }
    return Promise.reject(error);
  }
);

// Export the API methods
export async function apiGet(endpoint, headers = {}) {
  return apiClient.get(endpoint, { headers });
}

export async function apiPost(endpoint, body = {}, headers = {}) {
  return apiClient.post(endpoint, body, { headers });
}

export async function apiPut(endpoint, body = {}, headers = {}) {
  return apiClient.put(endpoint, body, { headers });
}

export async function apiDelete(endpoint, headers = {}) {
  return apiClient.delete(endpoint, { headers });
}

// Base64 Encode
export function encodeBase64(data) {
  return btoa(data);
}

// Base64 Decode
export function decodeBase64(data) {
  return atob(data);
}
